/**
 * O modal de um acontecimento: título, a janela de dias, nota, cor e as cinco
 * referências opcionais ao mundo — evento, NPC, local, facção e criatura.
 *
 * <p>Os seletores usam {@code permitirCriar={false}} pela mesma razão das Cenas: o
 * calendário referencia o conteúdo e nunca o escreve. Um botão que criasse NPC a
 * partir daqui seria a porta dos fundos que este módulo não pode ter.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import * as mundoApi from '../mundoApi';
import { SeletorDeRelacao } from '../SeletorDeRelacao';
import type { Colecao, Resumo } from '../tipos';
import type { Acontecimento } from './calendarioTipos';
import '../../modal.css';

/** Paleta curta, no vocabulário do painel. A API valida o formato de toda forma. */
const PALETA = ['#C9A25A', '#B04A45', '#4E9C6B', '#5B8FB9', '#A87BD0', '#3FA0A0'];

interface Referencia {
  chave: 'refEvento' | 'refNpc' | 'refLocal' | 'refFaccao' | 'refCriatura';
  alvo: Colecao;
  rotulo: string;
}

const REFERENCIAS: Referencia[] = [
  { chave: 'refEvento', alvo: 'eventos', rotulo: 'Evento' },
  { chave: 'refNpc', alvo: 'npcs', rotulo: 'NPC' },
  { chave: 'refLocal', alvo: 'locais', rotulo: 'Local' },
  { chave: 'refFaccao', alvo: 'faccoes', rotulo: 'Facção' },
  { chave: 'refCriatura', alvo: 'criaturas', rotulo: 'Criatura' },
];

/**
 * Um seletor de referência única que também sabe DEVOLVER o resumo escolhido: o
 * {@code SeletorDeRelacao} entrega só ids no {@code aoMudar}, e o modal precisa do
 * nome para o chip. A listagem vem da mesma chave de cache das telas do mundo
 * ({@code ['mundo', colecao]}), então normalmente já está carregada.
 */
function CampoDeReferencia({
  alvo,
  rotulo,
  escolhido,
  aoMudar,
  aoAbrir,
}: {
  alvo: Colecao;
  rotulo: string;
  escolhido: Resumo | null;
  aoMudar(resumo: Resumo | null): void;
  aoAbrir(colecao: Colecao, id: number): void;
}) {
  const { data: candidatos } = useQuery<Resumo[]>({
    queryKey: ['mundo', alvo],
    queryFn: () => mundoApi.listar(alvo),
    staleTime: 30_000,
  });

  return (
    <div className="fi-campo larga">
      <label>{rotulo}</label>
      <SeletorDeRelacao
        alvo={alvo}
        unico
        permitirCriar={false}
        selecionados={escolhido ? [escolhido] : []}
        aoMudar={(ids) =>
          aoMudar(ids.length === 0 ? null : (candidatos?.find((c) => c.id === ids[0]) ?? null))
        }
        aoAbrir={aoAbrir}
      />
    </div>
  );
}

export function ModalDeAcontecimento({
  acontecimento,
  diaInicial,
  diasTotal,
  salvando,
  erro,
  aoSalvar,
  aoExcluir,
  aoFechar,
}: {
  /** Nulo = criação (a partir do clique num dia). */
  acontecimento: Acontecimento | null;
  diaInicial: number;
  diasTotal: number;
  salvando: boolean;
  erro: string | null;
  aoSalvar(corpo: Record<string, unknown>): void;
  aoExcluir(): void;
  aoFechar(): void;
}) {
  const navegar = useNavigate();
  const [nome, setNome] = useState(acontecimento?.nome ?? '');
  const [inicio, setInicio] = useState(acontecimento?.diaInicio ?? diaInicial);
  const [fim, setFim] = useState(acontecimento?.diaFim ?? diaInicial);
  const [nota, setNota] = useState(acontecimento?.nota ?? '');
  const [cor, setCor] = useState<string | null>(acontecimento?.cor ?? null);
  const [refs, setRefs] = useState<Record<string, Resumo | null>>({
    refEvento: acontecimento?.refEvento ?? null,
    refNpc: acontecimento?.refNpc ?? null,
    refLocal: acontecimento?.refLocal ?? null,
    refFaccao: acontecimento?.refFaccao ?? null,
    refCriatura: acontecimento?.refCriatura ?? null,
  });

  const dias = Array.from({ length: diasTotal }, (_, i) => i + 1);

  function enviar() {
    if (!nome.trim()) return;
    const corpo: Record<string, unknown> = {
      nome: nome.trim(),
      diaInicio: Math.min(inicio, fim),
      diaFim: Math.max(inicio, fim),
      nota: nota.trim() || (acontecimento ? '' : undefined),
    };

    if (acontecimento) {
      // PATCH: cor e referências seguem o contrato id+remover do mundo.
      if (cor) corpo.cor = cor;
      else if (acontecimento.cor) corpo.removerCor = true;
      for (const { chave } of REFERENCIAS) {
        const escolhido = refs[chave];
        const original = acontecimento[chave as keyof Acontecimento] as Resumo | undefined;
        if (escolhido) corpo[`${chave}Id`] = escolhido.id;
        else if (original) corpo[`remover${chave[0].toUpperCase()}${chave.slice(1)}`] = true;
      }
    } else {
      if (cor) corpo.cor = cor;
      for (const { chave } of REFERENCIAS) {
        if (refs[chave]) corpo[`${chave}Id`] = refs[chave]!.id;
      }
    }
    aoSalvar(corpo);
  }

  return (
    <div className="backdrop" onClick={(e) => e.target === e.currentTarget && aoFechar()}>
      <div className="modal cal-modal" role="dialog" aria-modal="true">
        <div className="mhead">
          <div className="mtop">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="mchip">
                <span className="sw" style={{ background: cor ?? 'var(--gold)' }} />
                Acontecimento
              </div>
              <input
                className="mtitle-in"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex.: Invasão dos Profundos"
                autoFocus={!acontecimento}
              />
            </div>
            <button className="mclose" onClick={aoFechar} aria-label="Fechar">
              ×
            </button>
          </div>

          <div className="mwin">
            <div className="wsel">
              <label>Do dia</label>
              <select value={inicio} onChange={(e) => setInicio(Number(e.target.value))}>
                {dias.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
            <div className="warr">→</div>
            <div className="wsel">
              <label>Ao dia</label>
              <select value={fim} onChange={(e) => setFim(Number(e.target.value))}>
                {dias.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
            <div className="wdur">
              {Math.abs(fim - inicio) + 1} {Math.abs(fim - inicio) === 0 ? 'dia' : 'dias'}
            </div>
            {acontecimento && (
              <button className="wdel" onClick={aoExcluir}>
                Excluir
              </button>
            )}
          </div>

          <div className="cal-paleta">
            <span className="rl">Cor</span>
            {PALETA.map((amostra) => (
              <button
                key={amostra}
                type="button"
                className={`cal-cor${cor === amostra ? ' on' : ''}`}
                style={{ background: amostra }}
                title={amostra}
                onClick={() => setCor(amostra)}
              />
            ))}
            <button
              type="button"
              className={`cal-cor vazia${cor == null ? ' on' : ''}`}
              title="Sem cor (usa a padrão)"
              onClick={() => setCor(null)}
            >
              ∅
            </button>
          </div>
        </div>

        <div className="mbody">
          <div className="fi-campo larga">
            <label>Nota</label>
            <textarea
              className="fi-area"
              rows={2}
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              placeholder="O que acontece, para quem lê o calendário de relance."
            />
          </div>

          {REFERENCIAS.map(({ chave, alvo, rotulo }) => (
            <CampoDeReferencia
              key={chave}
              alvo={alvo}
              rotulo={rotulo}
              escolhido={refs[chave]}
              aoMudar={(resumo) => setRefs((r) => ({ ...r, [chave]: resumo }))}
              aoAbrir={(colecao, id) => navegar(`/mundo/${colecao}/${id}`)}
            />
          ))}
        </div>

        <div className="mfoot">
          {erro && <div className="fi-aviso">{erro}</div>}
          <div className="addrow">
            <button className="addbtn" disabled={salvando || !nome.trim()} onClick={enviar}>
              {salvando ? 'Salvando…' : acontecimento ? 'Salvar' : 'Criar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
